"use client"

import { trackScrollDepth } from "@/lib/analytics"
import { Button } from "@/components/ui/button"
import {
  X, Download, ExternalLink, FileText, Maximize2, RotateCw,
  ZoomIn, ZoomOut, Printer, Search, BookOpen
} from "lucide-react"
import { useState, useRef, useEffect, useMemo } from "react"
import { BoldTextRenderer } from "./bold-text-renderer"

interface PDFReaderProps {
  pdfUrl: string
  title: string
  onClose: () => void
  /** Enables summary scroll-depth events (plain-text summary view). */
  bookSlug?: string
}

type ScrollDepthKey = "25%" | "50%" | "75%" | "100%"

export function PDFReader({ pdfUrl, title, onClose, bookSlug }: PDFReaderProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [rotation, setRotation] = useState(0)
  const [zoom, setZoom] = useState(100)
  const [contentType, setContentType] = useState<'application/pdf' | 'text/plain' | null>(null)
  const [textContent, setTextContent] = useState<string>("")
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState<number[]>([])
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0)
  
  const contentRef = useRef<HTMLDivElement>(null)
  const summaryScrollContainerRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const scrollDepthFired = useRef<Record<ScrollDepthKey, boolean>>({
    "25%": false,
    "50%": false,
    "75%": false,
    "100%": false,
  })

  useEffect(() => {
    let isMounted = true
    
    const fetchContent = async () => {
      setIsLoading(true)
      setError(null)
      
      try {
        const response = await fetch(pdfUrl, { 
          cache: "no-store",
          headers: {
            'Accept': 'application/pdf, text/plain, */*'
          }
        })
        
        if (!response.ok) {
          throw new Error(`Failed to fetch content: ${response.statusText}`)
        }
        
        const contentType = response.headers.get('Content-Type') || ''
        
        if (contentType.includes('application/pdf')) {
          setContentType('application/pdf')
        } else {
          const text = await response.text()
          if (isMounted) {
            setContentType('text/plain')
            setTextContent(text)
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load content')
          setContentType('text/plain')
          setTextContent('Failed to load document content.')
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchContent()
    
    return () => {
      isMounted = false
    }
  }, [pdfUrl])

  useEffect(() => {
    scrollDepthFired.current = {
      "25%": false,
      "50%": false,
      "75%": false,
      "100%": false,
    }
  }, [pdfUrl, bookSlug])

  // Summary scroll depth (plain-text “summary page” — book detail modal)
  useEffect(() => {
    if (!bookSlug || contentType !== "text/plain" || isLoading || error) return
    const el = summaryScrollContainerRef.current
    if (!el) return

    let raf = 0
    const measure = () => {
      const scrollable = el.scrollHeight - el.clientHeight
      const ratio = scrollable <= 0 ? 1 : el.scrollTop / scrollable
      const fire = (min: number, depth: ScrollDepthKey) => {
        if (ratio + 0.005 >= min && !scrollDepthFired.current[depth]) {
          scrollDepthFired.current[depth] = true
          trackScrollDepth({ depth, book_slug: bookSlug })
        }
      }
      fire(0.25, "25%")
      fire(0.5, "50%")
      fire(0.75, "75%")
      fire(1, "100%")
    }

    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        measure()
      })
    }

    el.addEventListener("scroll", onScroll, { passive: true })
    measure()
    return () => {
      el.removeEventListener("scroll", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [bookSlug, contentType, isLoading, error, textContent])

  // Search functionality
  useEffect(() => {
    if (!searchQuery || contentType !== 'text/plain') {
      setSearchResults([])
      setCurrentSearchIndex(0)
      return
    }

    const regex = new RegExp(searchQuery, 'gi')
    const matches = []
    let match
    
    while ((match = regex.exec(textContent)) !== null) {
      matches.push(match.index)
    }
    
    setSearchResults(matches)
    setCurrentSearchIndex(0)
  }, [searchQuery, textContent, contentType])

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = pdfUrl
    link.download = `${title}.${contentType === 'application/pdf' ? 'pdf' : 'txt'}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleOpenInNewTab = () => {
    window.open(pdfUrl, '_blank')
  }

  const handlePrint = () => {
    if (contentType === 'application/pdf') {
      const printWindow = window.open(pdfUrl, '_blank')
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
      }
    } else if (contentRef.current) {
      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${title}</title>
              <style>
                body { 
                  font-family: 'Georgia', serif; 
                  line-height: 1.6; 
                  margin: 20mm; 
                  font-size: 14px;
                  color: #333;
                }
                pre { 
                  white-space: pre-wrap; 
                  word-wrap: break-word;
                  font-size: ${zoom / 100}em;
                }
                @media print {
                  body { margin: 15mm; }
                }
              </style>
            </head>
            <body>
              <h1>${title}</h1>
              <pre>${textContent}</pre>
            </body>
          </html>
        `)
        printWindow.document.close()
        printWindow.focus()
        setTimeout(() => {
          printWindow.print()
          printWindow.close()
        }, 500)
      }
    }
  }

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  const rotateContent = () => {
    setRotation((prev) => (prev + 90) % 360)
  }

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 300))
  }

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50))
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query)
  }

  const jumpToSearchResult = (index: number) => {
    if (searchResults.length === 0) return
    
    const targetIndex = Math.max(0, Math.min(index, searchResults.length - 1))
    setCurrentSearchIndex(targetIndex)
    
    const resultIndex = searchResults[targetIndex]
    if (contentRef.current && resultIndex !== undefined) {
      // Find the highlighted mark element
      const highlightedElements = contentRef.current.querySelectorAll('mark')
      const targetElement = highlightedElements[targetIndex]
      
      if (targetElement) {
        // Scroll the highlighted element into view
        targetElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        })
        
        // Add a temporary highlight to show which result is active
        highlightedElements.forEach((el, i) => {
          el.classList.remove('ring-2', 'ring-blue-500')
          if (i === targetIndex) {
            el.classList.add('ring-2', 'ring-blue-500')
          }
        })
      } else {
        // Fallback: scroll based on text position
        const textNode = contentRef.current.querySelector('pre, div')
        if (textNode) {
          // Create a temporary span to measure the position
          const tempSpan = document.createElement('span')
          const beforeText = textContent.substring(0, resultIndex)
          tempSpan.textContent = beforeText
          tempSpan.style.visibility = 'hidden'
          tempSpan.style.position = 'absolute'
          tempSpan.style.whiteSpace = 'pre-wrap'
          tempSpan.style.fontSize = `${zoom / 100}em`
          tempSpan.style.lineHeight = '1.7'
          tempSpan.style.fontFamily = 'ui-serif, Georgia, serif'
          
          textNode.appendChild(tempSpan)
          const rect = tempSpan.getBoundingClientRect()
          textNode.removeChild(tempSpan)
          
          // Scroll to the position
          const container = textNode.parentElement
          if (container) {
            const containerRect = container.getBoundingClientRect()
            const scrollTop = container.scrollTop + (rect.top - containerRect.top) - (containerRect.height / 2)
            container.scrollTo({ top: scrollTop, behavior: 'smooth' })
          }
        }
      }
    }
  }

  // Function to highlight search results in text
  const highlightSearchResults = (text: string, query: string) => {
    if (!query.trim()) return text
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    return text.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800 px-0.5 rounded">$1</mark>')
  }

  const pdfViewerUrl = useMemo(() => {
    if (contentType !== 'application/pdf') return ""
    return `${pdfUrl}#zoom=${zoom}&toolbar=1&navpanes=1&scrollbar=1`
  }, [pdfUrl, zoom, contentType])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`bg-background border-0 shadow-2xl rounded-lg overflow-hidden flex flex-col ${
          isFullscreen 
            ? 'w-[99vw] h-[99vh]' 
            : 'w-[95vw] h-[95vh] lg:w-[90vw] lg:h-[90vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: stacked on xs; single toolbar row on sm+ (matches pre–mobile-fix desktop) */}
        <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 border-b border-slate-200 dark:border-slate-700 shrink-0 flex flex-col gap-2 px-3 py-2 sm:h-12 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-4 sm:py-0">
          {/* Left: document info + optional in-modal search */}
          <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm shrink-0">
                {contentType === 'application/pdf' ? (
                  <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                ) : (
                  <BookOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                )}
              </div>
              <div className="min-w-0">
                <h2 className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-slate-100 truncate max-w-[180px] sm:max-w-[min(280px,32vw)]">
                  {title.split(' - ')[0]}
                </h2>
                <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400">
                  {contentType === 'application/pdf' ? 'PDF Document' : 'Text Document'}
                </p>
              </div>
            </div>

            {contentType === 'text/plain' && (
              <div className="flex items-center gap-2 min-w-0 sm:ml-4">
                <div className="relative">
                  <Search className="h-3.5 w-3.5 sm:h-4 sm:w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    ref={searchRef}
                    type="text"
                    placeholder="Search in document..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-8 pr-3 py-1.5 h-8 text-xs border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-[min(100%,170px)] sm:w-48"
                  />
                </div>
                {searchResults.length > 0 && (
                  <div className="flex items-center gap-1 text-[10px] sm:text-xs text-slate-500 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => jumpToSearchResult(currentSearchIndex - 1)}
                      disabled={currentSearchIndex === 0}
                      className="h-6 w-6 p-0"
                    >
                      ↑
                    </Button>
                    <span>{currentSearchIndex + 1}/{searchResults.length}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => jumpToSearchResult(currentSearchIndex + 1)}
                      disabled={currentSearchIndex === searchResults.length - 1}
                      className="h-6 w-6 p-0"
                    >
                      ↓
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: all actions in one row on desktop */}
          <div className="flex flex-wrap items-center gap-1 border-t border-slate-200/80 pt-2 dark:border-slate-600/80 sm:border-t-0 sm:pt-0 sm:flex-nowrap sm:justify-end sm:shrink-0">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleZoomOut}
                className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-slate-200 dark:hover:bg-slate-700"
                title="Zoom Out"
              >
                <ZoomOut className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
              <span className="px-1.5 sm:px-2 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400 min-w-[46px] sm:min-w-[50px] text-center">
                {zoom}%
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleZoomIn}
                className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-slate-200 dark:hover:bg-slate-700"
                title="Zoom In"
              >
                <ZoomIn className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
            </div>

            <div className="hidden sm:block w-px h-5 bg-slate-300 dark:bg-slate-600 mx-1 sm:mx-2" />

            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={rotateContent}
                className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-slate-200 dark:hover:bg-slate-700"
                title="Rotate Content"
              >
                <RotateCw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleFullscreen}
                className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-slate-200 dark:hover:bg-slate-700 hidden sm:inline-flex"
                title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrint}
                className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-slate-200 dark:hover:bg-slate-700"
                title="Print Content"
              >
                <Printer className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
            </div>

            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownload}
                className="h-7 sm:h-8 px-2 sm:px-3 text-xs sm:text-sm hover:bg-slate-200 dark:hover:bg-slate-700"
                title="Download Content"
              >
                <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4 sm:mr-1" />
                <span className="hidden sm:inline">Download</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleOpenInNewTab}
                className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-slate-200 dark:hover:bg-slate-700"
                title="Open in New Tab"
              >
                <ExternalLink className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                title="Close"
              >
                <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content area: min-h-0 keeps flex child scrollable on desktop */}
        <div className="flex-1 min-h-0 relative bg-white dark:bg-slate-900 overflow-hidden">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm z-10">
              <div className="flex flex-col items-center gap-4">
                <div className="relative">
                  <div className="animate-spin rounded-full h-12 w-12 border-3 border-slate-200 dark:border-slate-700 border-t-blue-500"></div>
                  {contentType === 'application/pdf' ? (
                    <FileText className="h-6 w-6 text-blue-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                  ) : (
                    <BookOpen className="h-6 w-6 text-blue-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                  )}
                </div>
                <div className="text-center">
                  <p className="text-base font-medium text-slate-900 dark:text-slate-100">Loading Document...</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Please wait while we prepare your content</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-slate-900">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto">
                  <X className="h-8 w-8 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Failed to Load Document</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{error}</p>
                </div>
                <Button onClick={() => window.location.reload()} variant="outline">
                  Try Again
                </Button>
              </div>
            </div>
          )}

          {!isLoading && !error && contentType === 'application/pdf' && (
            <div
              className="w-full h-full"
              style={{ 
                transform: `rotate(${rotation}deg)`, 
                transformOrigin: 'center center' 
              }}
            >
              <iframe
                src={pdfViewerUrl}
                title={title}
                className="w-full h-full border-0"
                allow="fullscreen"
              />
            </div>
          )}

          {!isLoading && !error && contentType === 'text/plain' && (
            <div className="w-full h-full min-h-0 overflow-hidden relative">
              <div 
                ref={summaryScrollContainerRef}
                className="w-full h-full min-h-0 overflow-auto"
                style={{ 
                  transform: `rotate(${rotation}deg)`, 
                  transformOrigin: 'center center',
                  transition: 'transform 0.3s ease-in-out'
                }}
              >
                <div
                  ref={contentRef}
                  className="min-h-full px-4 py-4 sm:px-8 sm:py-6"
                  style={{
                    fontSize: `${zoom / 100}em`,
                    lineHeight: 1.75,
                    fontFamily: 'ui-serif, Georgia, serif'
                  }}
                >
                  {searchQuery.trim() ? (
                    <div 
                      className="whitespace-pre-wrap text-slate-900 dark:text-slate-100 break-words"
                      dangerouslySetInnerHTML={{ 
                        __html: highlightSearchResults(textContent, searchQuery) 
                      }}
                    />
                  ) : (
                    <div className="whitespace-pre-wrap text-slate-900 dark:text-slate-100 break-words">
                      <BoldTextRenderer text={textContent} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  )
}